import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PdlPersonModalComponent } from '../pdl-person-modal/pdl-person-modal.component';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { SociedadService } from '../../services/sociedades.service';
import { BeneficiaryService } from '../../services/beneficiary.service';
import { UsuarioService } from '../../services/Usuario.service';
import { Beneficiary, BeneficiaryIdentity } from '../../interfaces/beneficiary.interface';

interface SocietyOption {
  rfc: string;
  razonSocial: string;
}

interface ShareholderWithBeneficiaries {
  rfc: string;
  nombre: string;
  beneficiaries: Beneficiary[];
}

@Component({
  selector: 'app-pdl-person-file',
  templateUrl: './pdl-person-file.component.html',
  styleUrls: ['./pdl-person-file.component.css']
})
export class PdlPersonFileComponent implements OnInit {

  societies: SocietyOption[] = [];
  selectedSocietyRfc: string = '';
  selectedSocietyNombre: string = '';
  shareholdersWithBeneficiaries: ShareholderWithBeneficiaries[] = [];
  loading = false;

  @ViewChild('identityDocumentModal') identityDocumentModal: TemplateRef<any>;
  documentModalRef: any;
  documentPreviewUrl: string | null = null;
  documentContentType: string = '';
  documentLoading = false;
  documentErrorMessage: string = '';
  currentIdentityLabel: string = '';
  currentIdentityDetails: BeneficiaryIdentity | null = null;

  constructor(
    private modalService: NgbModal,
    private router: Router,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private sociedadService: SociedadService,
    private beneficiaryService: BeneficiaryService,
    private usuarioService: UsuarioService
  ) {}

  ngOnInit(): void {
    this.loadSocieties();
  }

  loadSocieties() {
    const rfcUsuario = localStorage.getItem('idUsuario');
    const obs = rfcUsuario === 'Tracer'
      ? this.sociedadService.getAll(0, 1000)
      : this.sociedadService.getAccesSociedad(rfcUsuario || '');

    obs.subscribe(
      (message: any) => {
        if (message?.httpStatus === 200 && message?.sociedades) {
          this.societies = message.sociedades.map((s: any) => ({
            rfc: s.rfc,
            razonSocial: s.razonSocial || s.rfc
          })).sort((a: SocietyOption, b: SocietyOption) => a.razonSocial.localeCompare(b.razonSocial));
        }
      },
      (err) => {
        this.toastr.error('Error al cargar las sociedades.');
      }
    );
  }

  onSocietyChange() {
    if (!this.selectedSocietyRfc) {
      this.shareholdersWithBeneficiaries = [];
      return;
    }
    const sel = this.societies.find(s => s.rfc === this.selectedSocietyRfc);
    this.selectedSocietyNombre = sel?.razonSocial || this.selectedSocietyRfc;
    this.loadBeneficiariesBySociety();
  }

  loadBeneficiariesBySociety() {
    if (!this.selectedSocietyRfc) return;
    this.loading = true;
    this.spinner.show();

    this.beneficiaryService.getAllBySociedad(this.selectedSocietyRfc, 1, 500).subscribe(
      (message: any) => {
        if (message?.httpStatus === 200) {
          const beneficiaries = (message.beneficiaries as Beneficiary[]) || [];
          const byRfc: { [rfc: string]: Beneficiary[] } = {};
          for (const b of beneficiaries) {
            const rfc = b?.shareholderUsuarioRfc || '';
            if (!byRfc[rfc]) byRfc[rfc] = [];
            byRfc[rfc].push(b);
          }
          const rfcs = Object.keys(byRfc);
          if (rfcs.length === 0) {
            this.loadAccionistasWhenNoBeneficiaries();
            return;
          }
          this.shareholdersWithBeneficiaries = rfcs.map(rfc => ({
            rfc,
            nombre: '',
            beneficiaries: byRfc[rfc]
          }));
          this.hydrateShareholderNames(rfcs);
        } else {
          this.shareholdersWithBeneficiaries = [];
          this.loading = false;
          this.spinner.hide();
        }
      },
      () => {
        this.toastr.error('Error al cargar los beneficiarios.');
        this.loading = false;
        this.spinner.hide();
      }
    );
  }

  private loadAccionistasWhenNoBeneficiaries() {
    this.sociedadService.getAllUsuariosSociedad(1, 500, this.selectedSocietyRfc).subscribe({
      next: (message: any) => {
        if (message?.httpStatus === 200 && message?.usuarioRolSociedades) {
          const list = message.usuarioRolSociedades as { rolesId?: number; usuarioRfc?: string }[];
          const accionistaRfcs = [
            ...new Set(
              list
                .filter(u => u?.usuarioRfc && u.rolesId != null && Number(u.rolesId) === 1)
                .map(u => u.usuarioRfc as string)
            )
          ];
          if (accionistaRfcs.length === 0) {
            this.shareholdersWithBeneficiaries = [];
            this.loading = false;
            this.spinner.hide();
            return;
          }
          this.shareholdersWithBeneficiaries = accionistaRfcs.map(rfc => ({
            rfc,
            nombre: '',
            beneficiaries: []
          }));
          this.hydrateShareholderNames(accionistaRfcs);
        } else {
          this.shareholdersWithBeneficiaries = [];
          this.loading = false;
          this.spinner.hide();
        }
      },
      error: () => {
        this.toastr.error('Error al cargar los accionistas de la sociedad.');
        this.shareholdersWithBeneficiaries = [];
        this.loading = false;
        this.spinner.hide();
      }
    });
  }

  private hydrateShareholderNames(rfcs: string[]) {
    let pending = rfcs.length;
    rfcs.forEach((rfc, idx) => {
      this.usuarioService.find(rfc).subscribe({
        next: (msg: any) => {
          if (msg?.httpStatus === 200 && msg?.usuarioModel) {
            this.shareholdersWithBeneficiaries[idx].nombre = msg.usuarioModel.nombre || rfc;
          } else {
            this.shareholdersWithBeneficiaries[idx].nombre = rfc;
          }
        },
        error: () => { this.shareholdersWithBeneficiaries[idx].nombre = rfc; },
        complete: () => {
          pending--;
          if (pending === 0) {
            this.loading = false;
            this.spinner.hide();
          }
        }
      });
    });
  }

  openAddPdlPerson(shareholder: ShareholderWithBeneficiaries) {
    const modalRef = this.modalService.open(PdlPersonModalComponent);
    modalRef.componentInstance.sociedadRfc = this.selectedSocietyRfc;
    modalRef.componentInstance.sociedadNombre = this.selectedSocietyNombre;
    modalRef.componentInstance.shareholderUsuarioRfc = shareholder.rfc;
    modalRef.componentInstance.shareholderNombre = shareholder.nombre;
    modalRef.result.then(
      () => this.loadBeneficiariesBySociety(),
      () => {}
    );
  }

  formatEmails(b: Beneficiary): string {
    return (b?.emails || []).map(e => e?.email).filter(Boolean).join(', ') || '-';
  }

  formatPhones(b: Beneficiary): string {
    return (b?.phones || []).map(p => p?.phoneNumber).filter(Boolean).join(', ') || '-';
  }

  formatRelatives(b: Beneficiary): string {
    const parts = (b?.relatives || [])
      .filter(r => r?.name)
      .map(r => r.relativeTypeName ? `${r.relativeTypeName}: ${r.name}` : r.name);
    return parts.length ? parts.join('; ') : '-';
  }

  openIdentityDocumentModal(beneficiary: Beneficiary, identity?: BeneficiaryIdentity) {
    this.documentPreviewUrl = null;
    this.documentContentType = '';
    this.documentErrorMessage = '';
    this.currentIdentityLabel = '';
    this.currentIdentityDetails = null;

    const identities = beneficiary?.identitys || [];
    const idToShow = identity?.id ?? identities.find(i => i?.id)?.id;

    if (!idToShow) {
      this.currentIdentityDetails = identities[0] || null;
      this.documentErrorMessage = 'No hay documento de identidad asociado a este beneficiario.';
      this.documentModalRef = this.modalService.open(this.identityDocumentModal, { size: 'lg' });
      return;
    }

    const ident = identity || identities.find(i => i?.id === idToShow);
    this.currentIdentityDetails = ident || null;
    this.currentIdentityLabel = ident?.identityTypeName
      ? `${ident.identityTypeName} - ${ident.idNumber || ''}`
      : 'Documento de identidad';

    this.documentLoading = true;
    this.documentModalRef = this.modalService.open(this.identityDocumentModal, { size: 'xl' });

    this.beneficiaryService.getIdentityDocument(idToShow).subscribe({
      next: (res) => {
        const blob = res.body;
        let contentType = res.headers.get('Content-Type') || ident?.contentType || '';
        if (!contentType && ident?.fileName) {
          const ext = ident.fileName.split('.').pop()?.toLowerCase();
          if (ext === 'pdf') contentType = 'application/pdf';
          else if (ext === 'jpg' || ext === 'jpeg') contentType = 'image/jpeg';
          else if (ext === 'png') contentType = 'image/png';
        }
        if (!contentType) contentType = 'application/pdf';
        if (blob && blob.size > 0) {
          this.documentPreviewUrl = URL.createObjectURL(blob);
          this.documentContentType = contentType;
        } else {
          this.documentErrorMessage = 'El documento está vacío o no está disponible.';
        }
      },
      error: () => {
        this.documentErrorMessage = 'No se pudo cargar el documento. Puede que no exista o no esté disponible.';
      },
      complete: () => {
        this.documentLoading = false;
      }
    });
  }

  closeIdentityDocumentModal() {
    if (this.documentPreviewUrl) {
      URL.revokeObjectURL(this.documentPreviewUrl);
      this.documentPreviewUrl = null;
    }
    this.documentModalRef?.close();
  }

  isPdfContent(): boolean {
    return !!(this.documentContentType && this.documentContentType.toLowerCase().includes('application/pdf'));
  }

  isImageContent(): boolean {
    if (!this.documentContentType) return false;
    const t = this.documentContentType.toLowerCase().split(';')[0].trim();
    return t.startsWith('image/');
  }

  getImageTypeLabel(): string {
    if (!this.documentContentType) return 'Imagen';
    const t = this.documentContentType.toLowerCase().split(';')[0].trim();
    if (t.includes('jpeg') || t.includes('jpg')) return 'JPEG';
    if (t.includes('png')) return 'PNG';
    return 'Imagen';
  }

  getDownloadFileName(): string {
    const name = this.currentIdentityDetails?.fileName;
    if (name) return name;
    const ext = this.isPdfContent() ? 'pdf' : (this.isImageContent() ? (this.documentContentType?.includes('png') ? 'png' : 'jpg') : 'bin');
    return `documento-identidad.${ext}`;
  }
}
